import { Dialog } from "@headlessui/react";
import { ChangeEvent, FormEvent, useEffect, useRef, useState } from "react";
import { useMutation, useQuery } from "react-query";
import { useTranslation } from "react-i18next";
import { Modal } from "components/Modal/Modal";
import { PrimaryButton } from "components/Button/PrimaryButton";
import { SecondaryButton } from "components/Button/SecondaryButton";
import { DeleteButton } from "components/Button/DeleteButton";
import { LoadingButtonSpinner } from "components/Loading/LoadingButton";
import { useClient } from "utils/client";
import { fetchSupportProjects, sendSupport, SupportRequest } from "./api";

const MAX_FILE_SIZE = 2 * 1024 * 1024;
const MAX_TOTAL_SIZE = 4 * 1024 * 1024;
const MAX_FILES = 3;
const inputClass =
  "mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-cyan-500 focus:ring-cyan-500";

function attachmentType(file: File): string {
  const extension = file.name.split(".").pop()?.toLowerCase();
  const expected =
    extension === "png"
      ? "image/png"
      : extension === "jpg" || extension === "jpeg"
        ? "image/jpeg"
        : extension === "pdf"
          ? "application/pdf"
          : "";
  const declared = file.type === "image/jpg" ? "image/jpeg" : file.type;
  return expected && (!declared || declared === expected) ? expected : "";
}

function routeProject(path: string): string {
  const match = /^\/projects\/([^/]+)/.exec(path);
  try {
    return match ? decodeURIComponent(match[1]) : "";
  } catch {
    return "";
  }
}

export const SupportDialog = ({
  accountId,
  pagePath,
  onClose,
}: {
  accountId: string;
  pagePath: string;
  onClose: () => void;
}) => {
  const { t, i18n } = useTranslation();
  const client = useClient();
  const projects = useQuery(
    ["support-projects", accountId],
    () => fetchSupportProjects(client, accountId),
    {
      retry: false,
      refetchOnMount: "always",
      refetchOnWindowFocus: false,
    },
  );
  const [message, setMessage] = useState("");
  const [selection, setSelection] = useState<string | null>(null);
  const [files, setFiles] = useState<File[]>([]);
  const [fileError, setFileError] = useState(false);
  const [reading, setReading] = useState(false);
  const [error, setError] = useState("");
  const [sent, setSent] = useState(false);
  const active = useRef(true);
  const readers = useRef(new Set<FileReader>());
  const fileInput = useRef<HTMLInputElement>(null);
  const sending = useRef(false);
  const mutation = useMutation(
    (request: SupportRequest) => sendSupport(client, request),
    { retry: false },
  );

  useEffect(() => {
    active.current = true;
    const pendingReaders = readers.current;
    return () => {
      active.current = false;
      pendingReaders.forEach((reader) => reader.abort());
      pendingReaders.clear();
    };
  }, []);

  const available = projects.isError
    ? []
    : (projects.data || []).filter(
        (p) => p.projectdata.crn.split(":")[2] === accountId,
      );
  const wanted = selection ?? routeProject(pagePath);
  const projectCrn = available.some((p) => p.projectdata.crn === wanted)
    ? wanted
    : "";
  const busy = reading || mutation.isLoading;
  const valid =
    message.trim().length > 0 && Array.from(message.trim()).length <= 5000;

  const chooseFiles = (event: ChangeEvent<HTMLInputElement>) => {
    const selected = Array.from(event.target.files || []);
    event.target.value = "";
    if (!selected.length) return;
    setFileError(false);
    const next = [...files, ...selected];
    if (
      next.length > MAX_FILES ||
      next.reduce((total, file) => total + file.size, 0) > MAX_TOTAL_SIZE ||
      selected.some(
        (file) =>
          !file.size || file.size > MAX_FILE_SIZE || !attachmentType(file),
      )
    ) {
      setFileError(true);
      return;
    }
    setFiles(next);
  };

  const readAttachment = (file: File) =>
    new Promise<NonNullable<SupportRequest["attachments"]>[number]>(
      (resolve, reject) => {
        const reader = new FileReader();
        readers.current.add(reader);
        reader.onloadend = () => readers.current.delete(reader);
        reader.onerror = () =>
          reject(new Error("Attachment could not be read"));
        reader.onabort = () =>
          reject(new Error("Attachment reading was cancelled"));
        reader.onload = () => {
          const base64 = String(reader.result).split(",")[1];
          if (!base64) reject(new Error("Attachment is empty"));
          else
            resolve({
              name: file.name,
              contentType: attachmentType(file),
              base64,
            });
        };
        reader.readAsDataURL(file);
      },
    );

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!valid || reading || projects.isLoading || sending.current || sent)
      return;
    sending.current = true;
    setError("");
    const request: SupportRequest = {
      message: message.trim(),
      pagePath: pagePath.split(/[?#]/)[0],
      language: i18n.language || "en",
      ...(projectCrn ? { projectCrn } : {}),
    };
    try {
      setReading(true);
      const attachments = await Promise.all(files.map(readAttachment));
      if (!active.current) return;
      if (attachments.length) request.attachments = attachments;
      await mutation.mutateAsync(request);
      if (!active.current) return;
      setFiles([]);
      setMessage("");
      setSent(true);
    } catch (failure) {
      if (!active.current) return;
      const code = (failure as { response?: { data?: { code?: string } } })
        .response?.data?.code;
      setError(
        code === "rate_limited"
          ? "rateLimited"
          : code === "invalid_attachment" || code === "payload_too_large"
            ? "fileError"
            : "sendError",
      );
    } finally {
      sending.current = false;
      if (active.current) setReading(false);
    }
  };

  return (
    <Modal
      show
      onClose={() => {
        if (!sending.current) onClose();
      }}
      content={
        <div className="mx-auto max-w-xl" data-sentry-mask>
          <Dialog.Title className="text-xl font-semibold">
            {t("support.title")}
          </Dialog.Title>
          {sent ? (
            <>
              <p role="status" className="mt-4 text-sm text-gray-600">
                {t("support.success")}
              </p>
              <div className="flex flex-row justify-end mt-8 gap-2">
                <PrimaryButton onClick={onClose}>
                  {t("support.close")}
                </PrimaryButton>
              </div>
            </>
          ) : (
            <form onSubmit={submit} className="mt-4 space-y-4">
              <p className="text-sm text-gray-600">{t("support.intro")}</p>
              <label className="block" htmlFor="support-message">
                {t("support.message")}
                <textarea
                  id="support-message"
                  className={inputClass}
                  rows={5}
                  value={message}
                  disabled={busy}
                  onChange={(e) => setMessage(e.target.value)}
                  required
                  aria-describedby="support-message-hint"
                />
              </label>
              <p id="support-message-hint" className="text-sm text-gray-500">
                {t("support.messageHint")}
              </p>
              <label className="block" htmlFor="support-project">
                {t("support.project")}
                <select
                  id="support-project"
                  className={inputClass}
                  value={projectCrn}
                  disabled={busy || projects.isLoading}
                  onChange={(e) => setSelection(e.target.value)}
                >
                  <option value="">{t("support.noProject")}</option>
                  {available.map((p) => (
                    <option key={p.projectdata.crn} value={p.projectdata.crn}>
                      {[
                        p.projectdata.product_name || p.projectdata.crn,
                        p.projectdata.serial_number,
                        p.manufacturer?.company,
                      ]
                        .filter(Boolean)
                        .join(" · ")}
                    </option>
                  ))}
                </select>
              </label>
              {projects.isLoading && (
                <p role="status">{t("support.loadingProjects")}</p>
              )}
              {projects.isError && (
                <p role="alert">{t("support.projectError")}</p>
              )}
              <div>
                <label className="block mb-2" htmlFor="support-attachments">
                  {t("support.attachments")}
                </label>
                <input
                  ref={fileInput}
                  id="support-attachments"
                  className="hidden"
                  type="file"
                  accept=".png,.jpg,.jpeg,.pdf,image/png,image/jpeg,application/pdf"
                  multiple
                  disabled={busy}
                  onChange={chooseFiles}
                  aria-describedby="support-attachments-hint"
                />
                <SecondaryButton
                  disabled={busy}
                  onClick={() => fileInput.current?.click()}
                >
                  {t("support.chooseFiles")}
                </SecondaryButton>
              </div>
              <p
                id="support-attachments-hint"
                className="text-sm text-gray-500"
              >
                {t("support.attachmentsHint")}
              </p>
              {files.length > 0 && (
                <ul className="space-y-2">
                  {files.map((file, index) => (
                    <li
                      key={`${file.name}-${index}`}
                      className="flex items-center justify-between gap-3 text-sm"
                    >
                      <span className="min-w-0 break-all">
                        {file.name} ({Math.ceil(file.size / 1024)} KB)
                      </span>
                      <DeleteButton
                        flat
                        small
                        disabled={busy}
                        onClick={() => {
                          setFiles((previous) =>
                            previous.filter(
                              (_, position) => position !== index,
                            ),
                          );
                          setFileError(false);
                        }}
                      >
                        <span aria-hidden="true">
                          {t("support.removeAttachment")}
                        </span>
                        <span className="sr-only">
                          {t("support.removeAttachmentNamed", {
                            name: file.name,
                          })}
                        </span>
                      </DeleteButton>
                    </li>
                  ))}
                </ul>
              )}
              {fileError && (
                <p role="alert" className="text-red-700">
                  {t("support.fileError")}
                </p>
              )}
              <div aria-live="polite">
                {error && (
                  <p role="alert" className="text-red-700">
                    {t(`support.${error}`)}
                  </p>
                )}
              </div>
              <div className="flex justify-end items-center space-x-3">
                <button
                  type="button"
                  className="px-6 text-sm text-gray-500 hover:text-gray-700 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                  disabled={busy}
                  onClick={onClose}
                >
                  {t("support.cancel")}
                </button>
                <PrimaryButton
                  type="submit"
                  disabled={!valid || busy || reading || projects.isLoading}
                >
                  <span className="inline-flex items-center gap-2">
                    {busy && <LoadingButtonSpinner />}
                    {t(busy ? "support.sending" : "support.send")}
                  </span>
                </PrimaryButton>
              </div>
            </form>
          )}
        </div>
      }
    />
  );
};
